export default class LinkHandler {
  constructor() {
    this.idleDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.idleTimer = null;
    this.logoURL = null;
    this.observer = null;
    this.isAlreadyRedirecting = false;

    this.setLogoURL();
    this.modifyLinks();
    this.cleanupUnwantedElements();
    this.setupIdleTimeout();
    this.setupMutationObserver();
    this.setupUnloadEvent();
  }

  setLogoURL() {
    const logoLink = document.querySelector('a.header__logo');
    if (!logoLink) return;
    try {
      const url = new URL(logoLink.href, window.location.origin);
      this.copyKioskParams(url);
      this.logoURL = url.toString();
    } catch {
      console.error('Invalid logo URL');
    }
  }

  modifyLinks() {
    document.querySelectorAll('a').forEach((link) => {
      const href = link.getAttribute('href');
      const dataTarget = link.getAttribute('data-target');

      if (href?.startsWith('#') && href !== '#') {
        return;
      }
      if ((!href || href === '#') && dataTarget !== 'modal-form') {
        this.removeLinkOrContainer(link);
        return;
      }
      if (link.target === '_blank' && dataTarget !== 'modal-form') {
        this.removeLinkOrContainer(link);
        return;
      }
      // Keep TopUsed detail/list links (AEM `/topused/` paths and Storybook `/top-used-` mocks).
      if (href && !this.isTopUsedLink(href) && dataTarget !== 'modal-form') {
        this.removeLinkOrContainer(link);
        return;
      }
      this.appendKioskMode(link);
    });
  }

  isTopUsedLink(href) {
    return href.includes('/topused/') || href.includes('/top-used');
  }

  copyKioskParams(url) {
    const current = new URLSearchParams(window.location.search);
    current.forEach((value, key) => {
      if (key.startsWith('kiosk') && value) {
        url.searchParams.set(key, value);
      }
    });
    url.searchParams.set('kioskMode', 'true');
  }

  appendKioskMode(link) {
    const href = link.href;
    try {
      const url = new URL(href, window.location.origin);
      this.copyKioskParams(url);
      link.href = url.toString();
    } catch {
      return;
    }
    if (link.classList.contains('go-back')) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = link.href;
      });
    }
  }

  removeLinkOrContainer(link) {
    const href = link.getAttribute('href') || '';
    if (href.startsWith('tel:') || href.startsWith('mailto:')) {
      const container = link.closest('div');
      if (link.classList.contains('btn') && container) {
        container.remove();
      } else {
        link.removeAttribute('href');
      }
      return;
    }

    const container = link.closest('div');
    const productListItems = container?.closest('.products-list-items');
    if (container && !container.classList.contains('content')) {
      if (container.classList.contains('no-gutters')) {
        link.href = '#';
      } else {
        container.remove();
      }
    } else {
      link.remove();
    }

    if (productListItems && this.isEmpty(productListItems)) {
      const section = productListItems.closest('section');
      if (section && section.querySelector('h2')) {
        section.remove();
      }
    }
  }

  isEmpty(elem) {
    return Array.from(elem.children).every((child) => child.textContent.trim() === '' && child.children.length === 0);
  }

  cleanupUnwantedElements() {
    document.querySelectorAll('[data-js-footer-nav]').forEach((el) => el.remove());
    document.querySelectorAll('button').forEach((btn) => {
      if (btn.querySelector('i.icon-print')) btn.remove();
    });
  }

  setupIdleTimeout() {
    this.resetIdleTimer();
    ['mousemove', 'keydown', 'scroll'].forEach((evt) => window.addEventListener(evt, () => this.resetIdleTimer()));
  }

  resetIdleTimer() {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      localStorage.removeItem('vuex');
      if (this.logoURL && !this.isAlreadyRedirecting) {
        this.isAlreadyRedirecting = true;
        const redirectURL = new URL(this.logoURL, window.location.origin);
        this.copyKioskParams(redirectURL);
        window.location.href = redirectURL.toString();
      }
    }, this.idleDuration);
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === 'childList') {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.modifyLinks();
              this.cleanupUnwantedElements();
            }
          });
        }
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  setupUnloadEvent() {
    window.addEventListener('beforeunload', () => {
      this.observer?.disconnect();
      clearTimeout(this.idleTimer);
    });
  }
}
